import { ElementRef, forwardRef } from "react";
import { Box, Flex } from "@zenchef/styled-system/jsx";
import { cx } from "@zenchef/styled-system/css";
import { aromaBanner } from "@zenchef/styled-system/recipes";
import { token } from "@zenchef/styled-system/tokens";

import ContextualIcon from "../ContextualIcon/ContextualIcon";
import { Text } from "../typography/Text";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import useWindowSize from "../../utils/useWindowSize";
import BannerAction from "./BannerAction";
import { getIcons, getTheme } from "./utils";
import { BannerProps } from "./types";

const Banner = forwardRef<ElementRef<"div">, BannerProps>(
  (
    {
      title,
      description,
      className,
      primaryButton,
      secondaryButton,
      illustration,
      logo,
      mobileLogo,
      removeAction,
      ...props
    },
    ref,
  ) => {
    const [aromaBannerVariantProps, restProps] =
      aromaBanner.splitVariantProps(props);
    const classes = aromaBanner(aromaBannerVariantProps);

    const { width: windowWidth } = useWindowSize();
    const isExtraSmallScreen =
      windowWidth <= parseInt(token("breakpoints.xs"), 10);

    const { colorTheme, form, isRemovable } = props;

    const [isBar, isOat, isBlue] = getTheme(form, colorTheme ?? "neutral");
    const iconName = getIcons(colorTheme ?? "neutral");

    return (
      <Box
        className={cx(classes.container, className)}
        {...restProps}
        ref={ref}
      >
        {logo ? (
          <div className={cx(classes.logo, classes.desktopLogo)}>{logo}</div>
        ) : null}
        {mobileLogo ? (
          <div className={cx(classes.logo, classes.mobileLogo)}>
            {mobileLogo}
          </div>
        ) : null}

        <Flex className={classes.content}>
          {illustration ? (
            <Box className={classes.illustration}>{illustration}</Box>
          ) : null}
          <Flex
            gap="gap.4"
            flex="auto"
            width="100%"
            justifyContent="space-between"
            flexDirection={isBar ? "row" : "column"}
            marginLeft={illustration && isBar ? "107px" : undefined}
          >
            <Flex className={classes.text}>
              {iconName ? (
                <div className={classes.contextualIconWrap}>
                  <ContextualIcon
                    size="large"
                    hierarchy="bolder"
                    colorTheme="transparent"
                    name={iconName}
                  />
                </div>
              ) : null}
              <Flex className={classes.textWrap}>
                {title ? <Text className={classes.title}>{title}</Text> : null}
                <Text className={classes.description}>{description}</Text>
              </Flex>
            </Flex>

            {!!secondaryButton || !!primaryButton ? (
              <Flex className={classes.buttons}>
                {secondaryButton ? (
                  <BannerAction
                    {...secondaryButton}
                    isBar={isBar}
                    isOat={isOat}
                    isBlue={isBlue}
                    iconOnly={isBar && isExtraSmallScreen}
                  />
                ) : null}
                {primaryButton ? (
                  <BannerAction
                    {...primaryButton}
                    isPrimary
                    isBar={isBar}
                    isOat={isOat}
                    isBlue={isBlue}
                    iconOnly={isBar && isExtraSmallScreen}
                  />
                ) : null}
              </Flex>
            ) : null}
          </Flex>
        </Flex>

        {isRemovable && removeAction ? (
          <Button
            hierarchy={isOat ? "secondary-subtler" : "reversed-subtler"}
            className={classes.removeTrigger}
            onClick={removeAction}
          >
            <Icon name="x" />
          </Button>
        ) : null}
      </Box>
    );
  },
);

export type { BannerProps };
export default Banner;
